import React, { useEffect, useState } from 'react';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import CalendarWrapper from '../../../../basic/calendar-wrapper/CalendarWrapper';
import { addToDate, ChronoUnitLegacy } from 'utils/time-utils';
import { getExtractedDataValue, remakeArrayIntoOtherInterface } from '../../../data-input-output-table/utils';
import { calculateStepTime } from '../../utils/utils';
import { TitleOverrideAndDescriptionTemplate } from '../../../../templates/DataTableCellTemplates';
import { easasAlert } from '../../../../fragments/easas-global-fragment/EasasGlobalAlerts';
import { ICombinedData } from '../../../data-input-output-table/common/api/interfaces/interfaces';

import { DatasetDataApi } from 'api/DatasetDataApi';
import { UpdateDatasetData } from 'api/model/requests';
import { useGetDataInfoRange } from 'api/api-hooks/datasets-hooks';

type AddDataDialog = {
    selectedDatasetID: any;
    firstLoadDate: Date;
    dateFrequency: ChronoUnitLegacy;
    closeDialog(): void;
};

const TIME_OPTIONS = [
    { label: 'Seconds', value: 'SECONDS' },
    { label: 'Minutes', value: 'MINUTES' },
    { label: 'Hours', value: 'HOURS' },
    { label: 'Days', value: 'DAYS' },
    { label: 'Months', value: 'MONTHS' },
];

const WARNING_LIMIT = 100;

const AddDataDialog = ({selectedDatasetID,firstLoadDate, dateFrequency, closeDialog }: AddDataDialog) => {
    const identifiers = selectedDatasetID.map((ds) => ds.identifier);

    const [dataQuery, setDataQuery] = useState<Array<any>>([]);

    const [rangeSelected, setRangeSelected] = useState(false);
    const [selectedTime, setSelectedTime] = useState('MINUTES');
    const [timeValue, setTimeValue] = useState(0);
    const [dateFrom, setDateFrom] = useState<Date>(addToDate(new Date(), -6, 'HOURS'));
    const [dateTo, setDateTo] = useState<Date>(new Date());
    const [valueCount, setValueCount] = useState<number>(0);

    const fetchQuery = useGetDataInfoRange(
        identifiers,
        { timeStart: dateTo.toISOString() || firstLoadDate.toISOString() },
        {
            enabled: dateTo && identifiers.length > 0,
        }
    );

    const titleColumnRender = (rowData: ICombinedData) => {
        return (
            <div>
                {TitleOverrideAndDescriptionTemplate({
                    title: rowData.title,
                    overriddenTitle: rowData.overriddenTitle,
                    description: rowData.description,
                })}
            </div>
        );
    };

    useEffect(() => {
        if (fetchQuery.data && !fetchQuery.isError && !fetchQuery.isLoading && fetchQuery.data?.length > 0) {
            setDataQuery([...remakeArrayIntoOtherInterface(fetchQuery.data, selectedDatasetID)]);
        }
    }, [fetchQuery.data]);

    useEffect(() => {
        fetchQuery.refetch();
    }, [dateTo])

    const updateHandler = () => {
        if (dataQuery[0].data.length > 0) {
            if (valueCount < WARNING_LIMIT) {
                updateData();
                return;
            }
            confirmDialog({
                message: `Do you want to add ${valueCount} points?`,
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: () => updateData(),
            });
        } else {
            easasAlert('warn', 'No changes made', 'warn');
        }
    };

    const updateData = () => {
        let tempArr: Array<UpdateDatasetData | undefined> = dataQuery.map((rowData) => {
            let tempOb = fetchQuery.data?.find((ob: any) => ob.identifier === rowData.identifier);

            if (tempOb && rowData.data.length > 0) {
                if (tempOb.data.length === 0 && !rowData.data[0].value) {
                    return;
                }
                if (tempOb.data.length > 0 && tempOb.data[0].value === rowData.data[0].value) {
                    return;
                }

                const objVal: { [k: string]: any } = {};

                if (rangeSelected) {
                    if (rowData.data.length > 0) {
                        for (let i = 0; i < rowData.data.length; i++) {
                            objVal[`${rowData.data[i].key}`] = rowData.data[i].value;
                        }
                    }
                } else {
                    let dateString = dateTo.toISOString();
                    if (rowData.data.length > 0) {
                        dateString = rowData.data[0].key;
                    }
                    objVal[`${dateString}`] = rowData.data[0].value;
                }
                return {
                    identifier: rowData.identifier,
                    values: objVal,
                };
            }
        });

        if (tempArr.length === 0) {
            easasAlert('warn', 'No changes made', 'warn');
            return;
        }

        DatasetDataApi.updateData(tempArr as Array<UpdateDatasetData>)
            .then(() => {
                easasAlert('success', 'Datasets updated', 'success');
                closeDialog();
            })
            .catch((er) => {
                console.error(er);
                easasAlert('error', 'Error while updating datasets', 'error');
                closeDialog();
            });
    };

    const valueBodyInputTemplate = (rowData: ICombinedData) => {
        let suffix = '';

        if (rowData.unit.length > 0 && rowData.unit !== ' ') {
            suffix = ' ' + rowData.unit;
        }

        return (
            <InputNumber
                value={getExtractedDataValue(rowData.data, rowData.decimalPlaces)}
                onValueChange={(e) => valueChangeHandler(rowData.identifier as string, e.value)}
                mode='decimal'
                minFractionDigits={rowData.decimalPlaces}
                maxFractionDigits={rowData.decimalPlaces}
                suffix={suffix}
                placeholder={suffix}
            />
        );
    };

    const valueChangeHandler = (identifier: string, newValue: any) => {
        const tempArr = dataQuery.map((item) => {
            if (item.identifier === identifier) {
                if (rangeSelected) {
                    let calculateFrom = dateFrom.getTime();
                    const calculateTo = dateTo.getTime();
                    if (timeValue > 0) {
                        const step = calculateStepTime(timeValue, selectedTime);
                        let dataArray = [];
                        if (selectedTime === 'MONTHS') {
                            while (dateFrom.getTime() <= dateTo.getTime()) {
                                dataArray.push({ key: `${dateFrom.toISOString()}`, value: newValue });
                                dateFrom.setMonth(dateFrom.getMonth() + step);
                            }
                        } else {
                            while (calculateFrom <= calculateTo) {
                                dataArray.push({ key: `${new Date(calculateFrom).toISOString()}`, value: newValue });
                                calculateFrom += step;
                            }
                        }

                        return {
                            ...item,
                            data: dataArray,
                        };
                    }
                } else {
                    return {
                        ...item,
                        data: item.data.length > 0 ? [{ ...item.data[0], value: newValue }] : [{ key: `${dateTo.toISOString()}`, value: newValue }],
                    };
                }
            }
            return item;
        });

        setDataQuery(tempArr);
    };

    useEffect(() => {
        let calculateFrom = dateFrom.getTime();
        const calculateTo = dateTo.getTime();
        if (timeValue > 0) {
            const step = calculateStepTime(timeValue, selectedTime);
            let count = 0;
            if (selectedTime === 'MONTHS') {
                while (dateFrom.getTime() <= dateTo.getTime()) {
                    dateFrom.setMonth(dateFrom.getMonth() + step);
                    count++;
                }
            } else {
                while (calculateFrom <= calculateTo) {
                    calculateFrom += step;
                    count++;
                }
            }
            setValueCount(count);
        }
    }, [timeValue, selectedTime]);

    const onCancelClickHandler = () => {
        closeDialog();
    };

    return (
        <>
            {fetchQuery.isError && <>Error...</>}

            {!fetchQuery.isError && (
                <div className='flex flex-column gap-3 mt-2'>
                    <div className='flex gap-3 align-items-center'>
                        <p className='mb-0'>Range</p>
                        <InputSwitch checked={rangeSelected} onChange={(e) => setRangeSelected(e.value)} />
                    </div>

                    <div>
                        {rangeSelected && <CalendarWrapper value={dateFrom} onChange={setDateFrom} dateFrequency={dateFrequency} />}
                        <CalendarWrapper value={dateTo} onChange={setDateTo} dateFrequency={dateFrequency} />
                    </div>
                    {rangeSelected && (
                        <div className='flex gap-2 align-items-center'>
                            <p className='mb-0'>Every: </p>
                            <InputNumber className='max-w-max' value={timeValue} onValueChange={(e) => setTimeValue(e.value)} />
                            <Dropdown placeholder='Time frame' options={TIME_OPTIONS} value={selectedTime} onChange={(e) => setSelectedTime(e.value)} />
                            <div>Expected values count: {valueCount}</div>
                        </div>
                    )}

                    <DataTable
                        value={dataQuery}
                        editMode='cell'
                        className='editable-cells-table'
                        responsiveLayout='scroll'
                        scrollHeight={'50vh'}
                        loading={fetchQuery.isLoading}
                        size='small'
                    >
                        <Column field={'title'} header='Dataset' body={titleColumnRender} style={{ width: '90%' }} />
                        <Column field={'unit'} header='Value' body={valueBodyInputTemplate} />
                    </DataTable>
                    <div className='button-container flex gap-2'>
                        <Button label='Update' icon='pi pi-pencil' onClick={updateHandler} className='p-button-success p-button-rounded p-button-sm' />
                        <ConfirmDialog />
                        <Button label='Cancel' icon='pi pi-times' onClick={() => onCancelClickHandler()} className='p-button-danger p-button-rounded p-button-sm' />
                    </div>
                </div>
            )}
        </>
    );
};

export default AddDataDialog;
